import { useCallback, useMemo, useState } from 'react';
import { AgentSession } from '../../api/events-client/events-client.type';
import { useTranslation } from 'react-i18next';
import type { FilterGroupRadio } from '../../components/filters/filter-panel.type';

/**
 * Hook for managing filters in the Agentic Process Sessions view.
 * Provides functionality for user-level filtering using radio buttons.
 *
 * @param {AgentSession[]} sessions - Array of agent sessions to filter
 * @returns {Object} An object containing:
 * - filteredSessions: Array of sessions filtered by current filters
 * - filterGroups: Array of filter groups for the filter panel
 * - handleResetFilters: Function to reset filters to default values
 */
const useAgenticProcessSessionsFilters = (sessions: AgentSession[]) => {
  const i18n = useTranslation();
  const [selectedUser, setSelectedUser] = useState<string>('all');

  // Extract unique users from sessions
  const users = useMemo(() => {
    const userMap = new Map<string, { id: string; name?: string; email?: string }>();

    sessions.forEach((session) => {
      if (session.user) {
        userMap.set(session.user.id, {
          id: session.user.id,
          name: session.user.name ?? undefined,
          email: session.user.email ?? undefined,
        });
      }
    });

    return Array.from(userMap.values()).sort((a, b) => {
      const an = a.name ?? a.email ?? '';
      const bn = b.name ?? b.email ?? '';
      return an.localeCompare(bn);
    });
  }, [sessions]);

  // Handle user selection
  const handleUserSelection = useCallback((value: string) => {
    setSelectedUser(value);
  }, []);

  // Reset all filters
  const handleResetFilters = useCallback(() => {
    setSelectedUser('all');
  }, []);

  // Filter sessions based on selected filters
  const filteredSessions = useMemo(() => {
    let filtered = sessions;

    // Filter by selected user
    if (selectedUser !== 'all') {
      filtered = filtered.filter((session) => (session.user ? session.user.id === selectedUser : false));
    }

    return filtered;
  }, [sessions, selectedUser]);

  // Create filter groups
  const filterGroups: FilterGroupRadio[] = useMemo(() => {
    const groups: FilterGroupRadio[] = [];

    // User filter group (only show if there are users)
    if (users.length > 0) {
      const userOptions = [
        {
          value: 'all',
          label: i18n.t('common.all_users'),
        },
        ...users.map((user) => {
          const name = user.name?.trim();
          const email = user.email?.trim();
          const label = name && email ? `${name} (${email})` : name || email || 'Unknown user';
          return { value: user.id, label };
        }),
      ];

      const userFilterGroup: FilterGroupRadio = {
        name: i18n.t('common.user'),
        icon: 'person',
        options: userOptions,
        selectedValue: selectedUser,
        onSelectionChange: handleUserSelection,
      };
      groups.push(userFilterGroup);
    }

    return groups;
  }, [users, selectedUser, handleUserSelection, i18n.t]);

  return {
    filteredSessions,
    filterGroups,
    handleResetFilters,
  };
};

export { useAgenticProcessSessionsFilters };
